import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tooltip,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useConnection } from '@sezenta/connection';
import {
  MODULE_LABELS,
  displayContractTypeName,
} from '../../context/ContractHelpers';
import { AdminPageHeader, StatusPill, TEAL, cardShadow } from './adminUi';

// Shared (cross-contract) reference lists + the 4 CR004 types. Estimate item 24.
// Zone is per-contract, so it's NOT here (managed inside each contract).
const SHARED_TYPES = ['State', 'Council', 'Suburb', 'Area'];
// CR004: Dumping Type, User Type and Other Collection Type are intentionally
// hidden from this admin picker for now — re-add them here to re-enable.
const NEW_TYPES = ['ContractType'];

// "ContractType" is the stored master-data value; surface it as "Project Type".
const TYPE_LABEL: Record<string, string> = { ContractType: 'Project Type' };
const label = (t: string) =>
  TYPE_LABEL[t] ?? t.replace(/([A-Z])/g, ' $1').trim();

interface Row {
  id: number;
  name?: string;
  status?: string;
  moduleKey?: string;
  extra?: { moduleKey?: string };
}

const CrossContractMasterData: React.FC = () => {
  const connection = useConnection();
  const [type, setType] = useState<string>('ContractType');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // server-side pagination + search
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const isContractType = type === 'ContractType';

  const load = useCallback(() => {
    setLoading(true);
    connection
      .get(`master-data/by-type/${type}`, {
        params: {
          page,
          limit: pageSize,
          search: debouncedSearch || undefined,
        },
      })
      .then((r) => {
        setRows(r.data?.data ?? []);
        setTotal(r.data?.total ?? 0);
      })
      .catch(() => message.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [connection, type, page, pageSize, debouncedSearch]);

  useEffect(() => load(), [load]);

  // changing the type resets to the first page
  useEffect(() => setPage(1), [type]);

  // debounce search → page 1
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: true });
    setOpen(true);
  };
  const openEdit = (row: Row) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      status: row.status !== 'Inactive',
      moduleKey: row.moduleKey ?? row.extra?.moduleKey,
    });
    setOpen(true);
  };

  const submit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // inline validation errors are shown by antd
    }
    const payload: any = {
      type,
      name: values.name,
      status: values.status === false ? 'Inactive' : 'Active',
    };
    if (isContractType && values.moduleKey)
      payload.moduleKey = values.moduleKey;
    setSubmitting(true);
    try {
      if (editing) {
        await connection.patch(`master-data/${editing.id}`, payload);
        message.success('Updated');
      } else {
        await connection.post('master-data', payload);
        message.success('Created');
      }
      setOpen(false);
      load();
    } catch {
      message.error('Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (row: Row) => {
    try {
      await connection.delete(`master-data/${type}/${row.id}`);
      message.success('Deleted');
      load();
    } catch {
      message.error('Delete failed');
    }
  };

  const columns = useMemo(() => {
    const base: any[] = [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 90 },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        // Project-type names get the display-only rename (e.g. "Bin Bag
        // Collection" → "Store Collection"); other types show as stored.
        render: isContractType
          ? (v: string) => displayContractTypeName(v)
          : undefined,
      },
    ];
    if (isContractType) {
      base.push({
        title: 'Module',
        key: 'module',
        render: (_: any, r: Row) => {
          const mk = r.moduleKey ?? r.extra?.moduleKey;
          return mk ? (MODULE_LABELS[mk] ?? mk) : '—';
        },
      });
    }
    base.push(
      {
        title: 'Status',
        key: 'status',
        render: (_: any, r: Row) => (
          <StatusPill active={r.status !== 'Inactive'} />
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 120,
        align: 'center' as const,
        render: (_: any, r: Row) => (
          <Space size="middle">
            <Tooltip title="Edit entry" placement="bottom">
              <Button
                shape="circle"
                icon={<EditOutlined />}
                onClick={() => openEdit(r)}
                style={{ color: TEAL, borderColor: TEAL }}
              />
            </Tooltip>
            <Popconfirm
              title="Delete this entry?"
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => remove(r)}
            >
              <Tooltip title="Delete entry" placement="bottom">
                <Button shape="circle" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    );
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isContractType, type]);

  return (
    <div className="w-full h-full p-6 overflow-auto bg-[#F7F9FB]">
      <AdminPageHeader
        title="Cross-Project Master Data"
        subtitle="Shared reference lists and project-level types, managed centrally."
        action={
          // Project types are bound to a code-level module via extra.moduleKey
          // (see libs/business/src/contract/module-registry.ts) — a new one is
          // only meaningful alongside a new module, so creating them from here
          // is disabled. Editing an existing type still works.
          <Tooltip
            title={
              isContractType
                ? 'Project types are defined by the available modules and can’t be added here'
                : undefined
            }
          >
            {/* antd needs a wrapper element for a tooltip on a disabled button */}
            <span>
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                onClick={openCreate}
                disabled={isContractType}
                style={isContractType ? undefined : { background: TEAL }}
              >
                New Entry
              </Button>
            </span>
          </Tooltip>
        }
      />

      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <span className="text-[#5E6870] text-[14px]">Type</span>
        <Select
          style={{ minWidth: 240 }}
          value={type}
          onChange={setType}
          options={[
            {
              label: 'Shared lists',
              options: SHARED_TYPES.map((t) => ({ value: t, label: label(t) })),
            },
            ...NEW_TYPES.map((t) => ({ value: t, label: label(t) })),
          ]}
        />
        <Input
          allowClear
          prefix={<SearchOutlined className="text-[#84919A]" />}
          placeholder="Search by name"
          className="max-w-[260px]"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div
        className="bg-white rounded-[10px] border border-[#EAECF0] p-2"
        style={cardShadow}
      >
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          columns={columns}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page,
            pageSize,
            total,
            pageSizeOptions: [10, 20, 50, 100],
            showSizeChanger: true,
            showTotal: (t) => `${t} entries`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </div>

      <Drawer
        title={`${editing ? 'Edit' : 'New'} ${label(type)}`}
        open={open}
        onClose={() => setOpen(false)}
        width={420}
        extra={
          <Button
            type="primary"
            loading={submitting}
            onClick={submit}
            style={{ background: TEAL }}
          >
            Save
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="Name" size="large" />
          </Form.Item>
          {isContractType && (
            <Form.Item
              name="moduleKey"
              label="Module"
              tooltip="The module this project type unlocks."
            >
              <Select
                allowClear
                placeholder="Select module"
                options={Object.entries(MODULE_LABELS).map(([value, l]) => ({
                  value,
                  label: l,
                }))}
              />
            </Form.Item>
          )}
          <Form.Item name="status" label="Active" valuePropName="checked">
            <Switch defaultChecked />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default CrossContractMasterData;
